import { useState } from "react";
import { useRouter } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";

import {
  AppModal,
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  ProtectedScreen,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { KIT_COLORS } from "~/features/items/labels";
import { useItems } from "~/state/items";
import { useKitActions, useKits } from "~/state/kits";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export default function KitsScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const kits = useKits();
  const items = useItems();
  const actions = useKitActions();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [color, setColor] = useState(KIT_COLORS[0]);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!name.trim()) return;
    try {
      const id = await actions.create({ name, notes, color });
      setName("");
      setNotes("");
      setError(null);
      setCreateOpen(false);
      router.push(`/kits/${id}` as never);
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Unable to create this kit.",
      );
    }
  }

  return (
    <ProtectedScreen
      title="Kits"
      subtitle="Unlimited groups — a van kit, a camera bag, a site box."
    >
      <Card gap="md">
        <SectionHeader
          title="Your kits"
          description="Kits are unlimited on every plan. The ten-item free limit is on gear, not groups."
        />
        {kits.length === 0 ? (
          <EmptyState
            title="No kits yet"
            description="Create a kit and drop items into it."
          />
        ) : (
          <Stack gap="none">
            {kits.map((kit, index) => {
              const count = items.filter(
                (item) => item.kit_id === kit.id,
              ).length;
              return (
                <ListRow
                  key={kit.id}
                  first={index === 0}
                  title={kit.name}
                  description={kit.notes ?? undefined}
                  onPress={() => router.push(`/kits/${kit.id}` as never)}
                  meta={
                    <Badge tone="neutral">
                      {count} {count === 1 ? "item" : "items"}
                    </Badge>
                  }
                />
              );
            })}
          </Stack>
        )}
        <Button
          align="start"
          iconName="plus"
          onPress={() => setCreateOpen(true)}
        >
          <ButtonText>New kit</ButtonText>
        </Button>
      </Card>

      <AppModal
        title="New kit"
        visible={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <Stack gap="md">
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Name
            </AppText>
            <TextField
              accessibilityLabel="Kit name"
              value={name}
              placeholder="Van kit, camera bag, site box..."
              onChangeText={setName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Notes (optional)
            </AppText>
            <TextField
              accessibilityLabel="Kit notes"
              value={notes}
              placeholder="What lives in this kit"
              multiline
              style={stylesheet.textarea}
              onChangeText={setNotes}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Colour
            </AppText>
            <View style={stylesheet.swatches}>
              {KIT_COLORS.map((swatch) => (
                <Pressable
                  key={swatch}
                  accessibilityRole="button"
                  accessibilityLabel={`Colour ${swatch}`}
                  onPress={() => setColor(swatch)}
                  style={[
                    stylesheet.swatch,
                    { backgroundColor: swatch },
                    color === swatch ? stylesheet.swatchSelected : null,
                  ]}
                />
              ))}
            </View>
          </View>
          {error ? (
            <AppText size="xs" tone="danger">
              {error}
            </AppText>
          ) : null}
          <Button
            iconName="plus"
            disabled={!name.trim()}
            onPress={() => void create()}
          >
            <ButtonText>Create kit</ButtonText>
          </Button>
        </Stack>
      </AppModal>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    field: { gap: spacing.xs },
    textarea: { minHeight: 86, textAlignVertical: "top" },
    swatches: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s },
    swatch: {
      width: 28,
      height: 28,
      borderRadius: radii.s,
      borderWidth: 1,
      borderColor: colors.border,
    },
    swatchSelected: {
      borderWidth: 2,
      borderColor: colors.heading,
    },
  });
